import { Button, Card, CardBody } from '@nextui-org/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react'
import { patchTankMemo } from '../../../api/tankInfo/memoChange'
import saveIcon from '../../../assets/svg/tankInfo/refresh.svg?url'
import { useTankInfoModalStore } from '../../../store/tankInfo/useTankInfoModalStore'

export type MemoCardProps = {
  tankId: string
}

export const MemoCard: FC<MemoCardProps> = ({ tankId }) => {
  const cardValues = useTankInfoModalStore((state) => state.cardValues)
  // Store에서 이미 계산된 memo 값 사용
  const memo = cardValues?.memo ?? ''
  const [currentMemo, setCurrentMemo] = useState<string>(memo)
  const queryClient = useQueryClient()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }

  const onChangeMemo = (e: ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentMemo(e.target.value)
    adjustTextareaHeight()
  }

  const { mutate } = useMutation<void, unknown, { tankId: string; memo: string }, null>({
    mutationFn: async (state) => await patchTankMemo(state.tankId, state.memo),
    onSuccess: async () => {
      // Invalidate tank information query to refetch updated memo
      await queryClient.invalidateQueries({ queryKey: ['tankInformation', tankId] })
    },
  })
  const onSave = () => {
    if (tankId === null) return
    mutate({ tankId: tankId, memo: currentMemo })
  }

  useEffect(() => {
    setCurrentMemo(memo)
    // Adjust height when memo changes
    setTimeout(adjustTextareaHeight, 0)
  }, [memo, setCurrentMemo])

  return (
    <Card
      className="
        rounded-lg col-span-1 md:col-span-2
        border border-tdpldarkgrey/60
        bg-tdplbisque
        w-full h-auto
        p-4 md:p-5
        shadow-card
      "
      data-card-type="memo"
      data-testid={`memo-card-${tankId}`}
    >
      <CardBody className="m-0 p-0 h-auto overflow-visible">
        <div className="flex justify-between items-center w-full p-0 m-0">
          <span className="text-tdpldarkgrey text-base font-bold flex-shrink-0">Memo</span>
          <Button
            isIconOnly
            className="h-fit w-fit bg-tdplbisque min-w-0 flex-shrink-0 ml-auto hidden"
            size="sm"
          >
            <img src={saveIcon} alt="" onClick={onSave} />
          </Button>
        </div>
        <textarea
          ref={textareaRef}
          className="
            m-0 mt-2 md:mt-3
            p-0
            border-0
            bg-tdplbisque
            font-sans
            text-wrap text-sm md:text-base
            break-words
            focus:outline-none
            resize-none
            overflow-auto
            w-full
          "
          rows={1}
          onChange={onChangeMemo}
          value={currentMemo}
          style={{
            height: 'auto',
            minHeight: '20px',
            maxHeight: '250px',
          }}
        />
      </CardBody>
    </Card>
  )
}
